import React from 'react';
import { Lock, Sparkles, Zap, ArrowRight, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { usePlan } from '../../hooks/usePlan';

const PROMOS = {
  reels_enabled: {
    title: 'AI Reels Studio',
    plan: 'Grow Plan',
    icon: Sparkles,
    color: 'cyan',
    description: 'Generate Instagram & Facebook reels directly from your inventory — 4 professional templates, AI captions, hashtags, branded music.',
    bullets: [
      'Vertical 9:16 reels in under 30 seconds',
      'AI-generated captions + 8–10 hashtags',
      '4 premium templates (Luxury, Fast Sell, Minimal, New Arrival)',
      'Download MP4 → upload to Instagram in 1 click',
    ],
  },
  marketplace_leads_enabled: {
    title: 'Marketplace Leads',
    plan: 'DrivePro Plan',
    icon: Zap,
    color: 'fuchsia',
    description: 'Centralize leads from CarDekho, OLX, CarTrade and more — auto-dedupe, auto-acknowledge, auto-follow-up via WhatsApp.',
    bullets: [
      'CSV upload from any marketplace',
      'AI-powered column normalization & deduplication',
      'Automated WhatsApp acknowledgment (Day-1)',
      'Day-2 & Day-5 follow-up automation',
      'Source tracking + telecaller routing',
    ],
  },
};

const THEMES = {
  cyan: {
    iconBg: 'bg-cyan-500/15',
    icon: 'text-cyan-300',
    pill: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
    btn: 'bg-cyan-500 hover:bg-cyan-400 text-slate-950',
    check: 'text-cyan-400',
  },
  fuchsia: {
    iconBg: 'bg-fuchsia-500/15',
    icon: 'text-fuchsia-300',
    pill: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
    btn: 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white',
    check: 'text-fuchsia-400',
  },
};

/**
 * Wraps a page's content. If the dealer has `feature` enabled → render children.
 * Otherwise → render a polished upgrade screen with the locked-feature pitch.
 *
 * Use it at the top of any page that should be feature-gated:
 *   <FeatureGate feature="reels_enabled">
 *     <ReelsPage />
 *   </FeatureGate>
 */
const FeatureGate = ({ feature, children }) => {
  const { plan, hasFeature, loading } = usePlan();

  if (loading && !plan) {
    return <div className="text-slate-400 text-center py-16">Loading…</div>;
  }
  if (hasFeature(feature)) {
    return children;
  }

  const promo = PROMOS[feature] || {
    title: 'Premium Feature',
    plan: 'an upgraded plan',
    icon: Lock,
    color: 'cyan',
    description: 'This feature is not enabled on your account.',
    bullets: [],
  };
  const Icon = promo.icon;
  const t = THEMES[promo.color] || THEMES.cyan;

  return (
    <div data-testid={`feature-gate-${feature}`} className="space-y-6">
      {/* Page header (keeps consistent layout with the actual page) */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className={`text-xs uppercase tracking-[0.2em] font-medium flex items-center gap-2 ${t.icon}`}>
            <Lock className="w-3.5 h-3.5" /> Locked
          </span>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            {promo.title}
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            {promo.description}
          </p>
        </div>
        <Link to="/dealers/dashboard">
          <Button variant="outline" className="border-slate-700 bg-slate-900/40 text-slate-200">
            Back to Dashboard
          </Button>
        </Link>
      </div>

      {/* Locked hero card */}
      <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 md:p-12 text-center max-w-3xl mx-auto">
        <div className={`mx-auto w-20 h-20 rounded-2xl ${t.iconBg} flex items-center justify-center mb-6`}>
          <Icon className={`w-10 h-10 ${t.icon}`} />
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border ${t.pill}`}>
          <Crown className="w-3 h-3" /> {promo.plan}
        </span>
        <h2 className="mt-4 text-3xl font-extrabold text-white">
          Upgrade to unlock {promo.title}
        </h2>
        <p className="mt-2 text-slate-400 max-w-lg mx-auto">
          You're on <span className="font-semibold text-slate-200">{plan?.plan?.label || 'Start'}</span> right now.
          Activate this feature to get instant access — no logout required.
        </p>

        {promo.bullets.length > 0 && (
          <ul className="mt-6 space-y-2 text-left max-w-md mx-auto">
            {promo.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <ArrowRight className={`w-4 h-4 mt-0.5 flex-shrink-0 ${t.check}`} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-8 flex gap-2 justify-center flex-wrap">
          <a
            href={`mailto:support@propitstop.in?subject=${encodeURIComponent('Upgrade request: ' + promo.title)}`}
            data-testid={`feature-gate-upgrade-${feature}`}
          >
            <Button className={`${t.btn} font-semibold rounded-xl px-6`}>
              <Crown className="w-4 h-4 mr-2" /> Upgrade to {promo.plan}
            </Button>
          </a>
          <Link to="/dealers/dashboard">
            <Button variant="outline" className="border-slate-700 bg-slate-900/40 text-slate-200 rounded-xl px-6">
              Maybe later
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FeatureGate;
